// The messages between the desktop app's screen and the cloud's page it shows
// inside it (an iframe): the app draws the rail, Personal and a workspace's
// sidebar (from the page's data, the outside's contract); the page draws the
// panel beside it (or the whole of a page with a sidebar of its own: the
// settings). Both ends check who sent a message before reading it (the page's
// origin and window on the app's side, the app's on the page's side), and
// only these messages go between them: where to go, how to look (the theme,
// the window controls over the page), the back and forward the app's window
// keeps (the swipe, the mouse's buttons), the outside's contract, and the
// page's notices, which the app shows as its own notifications. Nothing
// about what is sent to an agent.

/** The page → the app. */
export type FrameToHost =
  /** The page is up (sent once it has drawn). */
  | { type: "trameria-frame:ready" }
  /** The page moved to `href` (a path on the cloud's origin). */
  | { type: "trameria-frame:location"; href: string }
  /**
   * A page of personal data was opened in the page (a workspace's row of my
   * things, a Case's chip): the app shows it, beside the workspace's sidebar
   * (`workspace`, a slug, narrowed to `workspaceId`, its id) or in Personal (none).
   */
  | {
      type: "trameria-frame:open-personal";
      href: string;
      workspace: string | null;
      workspaceId: string | null;
    }
  /** The page's session is gone: the app makes it again and loads it anew. */
  | { type: "trameria-frame:signed-out" }
  /**
   * A wheel the page did not use (the app reads a two-finger swipe from
   * them), or the mouse's back / forward button: the app's back and forward.
   */
  | {
      type: "trameria-frame:wheel";
      deltaX: number;
      deltaY: number;
      deltaMode: number;
    }
  | { type: "trameria-frame:history-button"; direction: "back" | "forward" }
  /**
   * The person's workspaces changed (one made, joined, left, renamed, given
   * another icon, deleted): the app reads its rail's list again.
   */
  | { type: "trameria-frame:workspaces" }
  /**
   * The outside's contract (contract.ts) from the page: its menu with the
   * workspace's own sidebar as data, which the app draws. The app reads it
   * with the contract's readers.
   */
  | { type: "trameria-frame:shell"; message: Record<string, unknown> }
  /**
   * A notice for this desktop (a direct message, a mention) the page would
   * show: the app shows it as its notification unless the person is looking
   * at its page. `url` is a path on the cloud's origin.
   */
  | {
      type: "trameria-frame:notify";
      title: string;
      body: string;
      url: string;
      tag: string;
      /** Buttons (mac): each opens its own path on the cloud's origin. */
      actions?: { id: string; label: string; url: string }[];
    }
  /** The notifications with this tag are read: the app takes them away. */
  | { type: "trameria-frame:notices-clear"; tag: string }
  /** Open the OS's settings of the app's notifications. */
  | { type: "trameria-frame:notification-settings" };

/** The app → the page. */
export type HostToFrame =
  /** Go to `href` (a path on the cloud's origin), within the page. */
  | { type: "trameria-host:navigate"; href: string }
  /** The look chosen on this PC. */
  | { type: "trameria-host:theme"; theme: "auto" | "light" | "dark" }
  /**
   * How much of the page to draw: the panel alone beside the app's sidebar
   * (a workspace's pages, the direct messages), or all of it (a page with a
   * sidebar of its own: the settings, a new workspace).
   */
  | { type: "trameria-host:layout"; layout: FrameLayout }
  /**
   * The window's controls over the page, in the page's own coordinates (null:
   * none): a sidebar of its own starts below their strip, and a header row
   * reaching them keeps its end free.
   */
  | { type: "trameria-host:title-bar"; area: FrameTitleBar | null }
  /**
   * The outside's contract to the page (an entry of the workspace's sidebar
   * pressed, a change to its favorites), read with `readFromHost`.
   */
  | { type: "trameria-host:shell"; message: Record<string, unknown> }
  /**
   * The pointer rests on a link to `href` (a path on the cloud's origin):
   * the page loads what it shows ahead, so going there draws at once.
   */
  | { type: "trameria-host:preload"; href: string }
  /**
   * The app that shows the page's notices: its installation (the cloud's
   * destination for this desktop) and whether the OS lets it show them.
   * Sent once the page is up and when the OS's answer changes.
   */
  | {
      type: "trameria-host:desktop";
      installationId: string;
      notificationStatus: FrameNotificationStatus;
    };

export type FrameNotificationStatus = "ok" | "blocked" | "unavailable";

/** The longest texts a notice carries over (a notification shows a few lines). */
const NOTICE_MAX = { title: 1_000, body: 10_000, url: 8_000, tag: 256 };

const text = (v: unknown, max: number): v is string =>
  typeof v === "string" && v.length <= max;

/** The window controls' corners as the page sees them (TitleBarArea's fields). */
export interface FrameTitleBar {
  left: number;
  right: number;
  height: number;
}

const inBar = (v: unknown): v is number =>
  typeof v === "number" && Number.isFinite(v) && v >= 0 && v <= 400;

export type FrameLayout = { kind: "full" } | { kind: "panel" };

const finite = (v: unknown): v is number =>
  typeof v === "number" && Number.isFinite(v) && Math.abs(v) < 100_000;

const PATH = /^\/(?!\/)[^\s\\]*$/;

/** A message from the page, when it is one (its fields checked). */
export function parseFrameToHost(raw: unknown): FrameToHost | null {
  if (typeof raw !== "object" || raw === null) return null;
  const m = raw as Record<string, unknown>;
  switch (m.type) {
    case "trameria-frame:ready":
    case "trameria-frame:signed-out":
      return { type: m.type };
    case "trameria-frame:location":
      return typeof m.href === "string" && PATH.test(m.href)
        ? { type: m.type, href: m.href }
        : null;
    case "trameria-frame:workspaces":
    case "trameria-frame:notification-settings":
      return { type: m.type };
    case "trameria-frame:notify":
      return text(m.title, NOTICE_MAX.title) &&
        m.title !== "" &&
        text(m.body, NOTICE_MAX.body) &&
        text(m.url, NOTICE_MAX.url) &&
        PATH.test(m.url) &&
        text(m.tag, NOTICE_MAX.tag)
        ? {
            type: m.type,
            title: m.title,
            body: m.body,
            url: m.url,
            tag: m.tag,
            ...frameActions(m.actions),
          }
        : null;
    case "trameria-frame:notices-clear":
      return text(m.tag, NOTICE_MAX.tag) ? { type: m.type, tag: m.tag } : null;
    case "trameria-frame:shell":
      return typeof m.message === "object" && m.message !== null
        ? { type: m.type, message: m.message as Record<string, unknown> }
        : null;
    case "trameria-frame:wheel":
      return finite(m.deltaX) &&
        finite(m.deltaY) &&
        (m.deltaMode === 0 || m.deltaMode === 1 || m.deltaMode === 2)
        ? {
            type: m.type,
            deltaX: m.deltaX,
            deltaY: m.deltaY,
            deltaMode: m.deltaMode,
          }
        : null;
    case "trameria-frame:history-button":
      return m.direction === "back" || m.direction === "forward"
        ? { type: m.type, direction: m.direction }
        : null;
    case "trameria-frame:open-personal":
      return typeof m.href === "string" &&
        /^\/me(?:[/?#]|$)/.test(m.href) &&
        PATH.test(m.href)
        ? {
            type: m.type,
            href: m.href,
            workspace:
              typeof m.workspace === "string" &&
              /^[\w-]{1,64}$/.test(m.workspace)
                ? m.workspace
                : null,
            workspaceId:
              typeof m.workspaceId === "string" &&
              /^[\w-]{1,64}$/.test(m.workspaceId)
                ? m.workspaceId
                : null,
          }
        : null;
    default:
      return null;
  }
}

/** A message from the app, when it is one (its fields checked). */
export function parseHostToFrame(raw: unknown): HostToFrame | null {
  if (typeof raw !== "object" || raw === null) return null;
  const m = raw as Record<string, unknown>;
  switch (m.type) {
    case "trameria-host:navigate":
      return typeof m.href === "string" && PATH.test(m.href)
        ? { type: m.type, href: m.href }
        : null;
    case "trameria-host:theme":
      return m.theme === "auto" || m.theme === "light" || m.theme === "dark"
        ? { type: m.type, theme: m.theme }
        : null;
    case "trameria-host:layout": {
      const l = m.layout as { kind?: unknown } | null;
      return {
        type: m.type,
        layout: { kind: l?.kind === "panel" ? "panel" : "full" },
      };
    }
    case "trameria-host:preload":
      return typeof m.href === "string" && PATH.test(m.href)
        ? { type: m.type, href: m.href }
        : null;
    case "trameria-host:shell":
      return typeof m.message === "object" && m.message !== null
        ? { type: m.type, message: m.message as Record<string, unknown> }
        : null;
    case "trameria-host:desktop":
      return typeof m.installationId === "string" &&
        /^[\w-]{1,64}$/.test(m.installationId)
        ? {
            type: m.type,
            installationId: m.installationId,
            notificationStatus:
              m.notificationStatus === "blocked" ||
              m.notificationStatus === "unavailable"
                ? m.notificationStatus
                : "ok",
          }
        : null;
    case "trameria-host:title-bar": {
      const a = m.area as Record<string, unknown> | null;
      if (a === null) return { type: m.type, area: null };
      return typeof a === "object" &&
        inBar(a.left) &&
        inBar(a.right) &&
        inBar(a.height)
        ? {
            type: m.type,
            area: { left: a.left, right: a.right, height: a.height },
          }
        : null;
    }
    default:
      return null;
  }
}

/** The desktop app's screens' origins (its own scheme): the only ones that may frame the page. */
export const isAppOrigin = (origin: string) =>
  /^trameria(?:-[a-z]+)?:\/\/(?:app|hub)$/.test(origin);

/** A notice's buttons from the frame: paths only, a few at most. */
function frameActions(raw: unknown): {
  actions?: { id: string; label: string; url: string }[];
} {
  if (!Array.isArray(raw)) return {};
  const actions = raw.slice(0, 3).flatMap((a) => {
    const { id, label, url } = (a ?? {}) as Record<string, unknown>;
    return text(id, 64) &&
      text(label, 64) &&
      text(url, NOTICE_MAX.url) &&
      PATH.test(url)
      ? [{ id, label, url }]
      : [];
  });
  return actions.length ? { actions } : {};
}
